import {
  ColumnDefinitionInputV1,
  ColumnRuleAncestorInputV1,
  ColumnRuleAssetCreatorInputV1,
  ColumnRuleConcatInputV1,
  ColumnRuleConstantInputV1,
  ColumnRuleDescendantInputV1,
  ColumnRuleEventPropertyInputV1,
  ColumnRuleFormulaCreatorInputV1,
  ColumnRuleGetItemPropertyInputV1,
  ColumnRuleInputV1,
  ColumnRuleItemSearchInputV1,
  ColumnRuleManagerInputV1,
  ColumnRulePathInputV1,
  ColumnRulePathSearchInputV1,
  ColumnRuleScalarCreatorInputV1,
  ColumnRuleSearchAncestorInputV1,
  ColumnRuleSetItemPropertyInputV1,
  ColumnRuleTextExtractorInputV1,
  ColumnRuleTextReplacementInputV1,
  ColumnRuleTreePathCreatorInputV1,
} from '@/sdk';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { ColumnTypeEnum } from '@/sdk/model/ColumnDefinitionInputV1';
import { PropertyMatchOperatorEnum } from '@/sdk/model/ColumnRuleDescendantInputV1';
import {
  DecoratedScalingTableColumnDefinition,
  PotentialColumnDefinition,
  ScalingTableColumnDefinition,
  TableDefinitionActions,
} from '@/tableDefinitionEditor/tableDefinition.types';
import {
  ColumnRuleWithMetadata,
  ParameterWithMetadata,
} from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.types';
import { TableDefinitionStore } from '@/tableDefinitionEditor/tableDefinition.store';
import { ColumnRuleContextConditionInputV1 } from 'sdk/model/ColumnRuleContextConditionInputV1';

export type ColumnRuleInputParameters =
  | ColumnRuleGetItemPropertyInputV1
  | ColumnRulePathInputV1
  | ColumnRuleAncestorInputV1
  | ColumnRuleConcatInputV1
  | ColumnRuleDescendantInputV1
  | ColumnRuleEventPropertyInputV1
  | ColumnRuleFormulaCreatorInputV1
  | ColumnRuleAssetCreatorInputV1
  | ColumnRuleTreePathCreatorInputV1
  | ColumnRuleConstantInputV1
  | ColumnRuleSetItemPropertyInputV1
  | ColumnRulePathSearchInputV1
  | ColumnRuleTextExtractorInputV1
  | ColumnRuleTextReplacementInputV1
  | ColumnRuleScalarCreatorInputV1
  | ColumnRuleManagerInputV1
  | ColumnRuleContextConditionInputV1
  | ColumnRuleSearchAncestorInputV1;

export type CombinedColumnRuleInputParameters = ColumnRuleGetItemPropertyInputV1 &
  ColumnRulePathInputV1 &
  ColumnRuleAncestorInputV1 &
  ColumnRuleConcatInputV1 &
  ColumnRuleDescendantInputV1 &
  ColumnRuleEventPropertyInputV1 &
  ColumnRuleFormulaCreatorInputV1 &
  ColumnRuleAssetCreatorInputV1 &
  ColumnRuleTreePathCreatorInputV1 &
  ColumnRuleConstantInputV1 &
  ColumnRuleSetItemPropertyInputV1 &
  ColumnRulePathSearchInputV1 &
  ColumnRuleTextExtractorInputV1 &
  ColumnRuleTextReplacementInputV1 &
  ColumnRuleItemSearchInputV1 &
  ColumnRuleScalarCreatorInputV1 &
  ColumnRuleManagerInputV1 &
  ColumnRuleContextConditionInputV1 &
  ColumnRuleSearchAncestorInputV1 & { fullPath: boolean };

export interface ColumnRuleInput {
  ancestor?: ColumnRuleAncestorInputV1;
  assetCreator?: ColumnRuleAssetCreatorInputV1;
  concatColumns?: ColumnRuleConcatInputV1;
  constant?: ColumnRuleConstantInputV1;
  descendant?: ColumnRuleDescendantInputV1;
  eventProperty?: ColumnRuleEventPropertyInputV1;
  formulaCreator?: ColumnRuleFormulaCreatorInputV1;
  getItemProperty?: ColumnRuleGetItemPropertyInputV1;
  itemSearch?: ColumnRuleItemSearchInputV1;
  path?: ColumnRulePathInputV1;
  pathSearch?: ColumnRulePathSearchInputV1;
  scalarCreator?: ColumnRuleScalarCreatorInputV1;
  numericScalarCreator?: ColumnRuleScalarCreatorInputV1;
  stringScalarCreator?: ColumnRuleScalarCreatorInputV1;
  setItemProperty?: ColumnRuleSetItemPropertyInputV1;
  textExtractor?: ColumnRuleTextExtractorInputV1;
  textReplacement?: ColumnRuleTextReplacementInputV1;
  treePathCreator?: ColumnRuleTreePathCreatorInputV1;
  AssetTreeCreator?: ColumnRuleTreePathCreatorInputV1;
  AssetTreeAddChild?: ColumnRuleTreePathCreatorInputV1;
  contextCondition?: ColumnRuleContextConditionInputV1;
  searchAncestor?: ColumnRuleSearchAncestorInputV1;
}

export type ColumnRule = keyof ColumnRuleInput;
export type RuleParameter = keyof CombinedColumnRuleInputParameters;

// TODO CRAB-41437 unrestrict these permissions options
export const restrictedParams: string[] = [
  SeeqNames.MaterializedTables.Parameters.IdentityId,
  SeeqNames.MaterializedTables.Parameters.PermissionString,
];

export const ItemTypeOptions = [
  { value: SeeqNames.Types.Condition, label: 'ITEM_FINDER.TYPES_CONDITION' },
  { value: SeeqNames.Types.Signal, label: 'ITEM_FINDER.TYPES_SIGNAL' },
  { value: SeeqNames.Types.Scalar, label: 'ITEM_FINDER.TYPES_SCALAR' },
  { value: SeeqNames.Types.Asset, label: 'ITEM_FINDER.TYPES_ASSET' },
];

export const PropertyNameOptions = [
  { value: SeeqNames.Properties.Name, label: 'ITEM_FINDER.PROPERTY_SELECT_NAME' },
  { value: SeeqNames.Properties.Description, label: 'ITEM_FINDER.PROPERTY_SELECT_DESCRIPTION' },
  { value: SeeqNames.Properties.DatasourceId, label: 'ITEM_FINDER.PROPERTY_SELECT_DATASOURCE_ID' },
  { value: SeeqNames.Properties.DatasourceClass, label: 'ITEM_FINDER.PROPERTY_SELECT_DATASOURCE_CLASS' },
];

export const PropertyMatchOperatorEnumToLabel: Record<PropertyMatchOperatorEnum, string> = {
  [PropertyMatchOperatorEnum.EQUALS]: 'EQUALS',
  [PropertyMatchOperatorEnum.EQUALSIGNORECASE]: 'EQUALS_IGNORE_CASE',
  [PropertyMatchOperatorEnum.NOTEQUALS]: 'NOT_EQUALS',
  [PropertyMatchOperatorEnum.WITHIN]: 'SCALING.PROPERTY_OPERATORS.WITHIN',
  [PropertyMatchOperatorEnum.STRINGCONTAINS]: 'SCALING.PROPERTY_OPERATORS.STRING_CONTAINS',
  [PropertyMatchOperatorEnum.REGEXMATCHES]: 'SCALING.PROPERTY_OPERATORS.REGEX_MATCHES',
  [PropertyMatchOperatorEnum.NOTREGEXMATCHES]: 'SCALING.PROPERTY_OPERATORS.NOT_REGEX_MATCHES',
};

export const ColumnTypeOptions = [
  {
    value: ColumnTypeEnum.TEXT,
    label: 'SCALING.COLUMN_TYPE_OPTIONS.TEXT.LABEL',
    description: 'SCALING.COLUMN_TYPE_OPTIONS.TEXT.DESCRIPTION',
  },
  {
    value: ColumnTypeEnum.NUMERIC,
    label: 'SCALING.COLUMN_TYPE_OPTIONS.NUMERIC.LABEL',
    description: 'SCALING.COLUMN_TYPE_OPTIONS.NUMERIC.DESCRIPTION',
  },
  {
    value: ColumnTypeEnum.UUID,
    label: 'SCALING.COLUMN_TYPE_OPTIONS.UUID.LABEL',
    description: 'SCALING.COLUMN_TYPE_OPTIONS.UUID.DESCRIPTION',
  },
  {
    value: ColumnTypeEnum.TIMESTAMPTZ,
    label: 'SCALING.COLUMN_TYPE_OPTIONS.TIMESTAMPTZ.LABEL',
    description: 'SCALING.COLUMN_TYPE_OPTIONS.TIMESTAMPTZ.DESCRIPTION',
  },
  {
    value: ColumnTypeEnum.BOOLEAN,
    label: 'SCALING.COLUMN_TYPE_OPTIONS.BOOLEAN.LABEL',
    description: 'SCALING.COLUMN_TYPE_OPTIONS.BOOLEAN.DESCRIPTION',
  },
] as const;

export const AllColumnEnumOptions = Object.values(ColumnTypeEnum) as ColumnTypeEnum[];

export interface BaseInputParametersProps {
  setParameter: (parameter: RuleParameter, value: any) => void;
  testId: string;
  ruleParameterWithMetadata: ParameterWithMetadata;
  currentValue?: any;
  extraClassNames?: string;
  ruleType: ColumnRuleWithMetadata;
  columnType?: ColumnTypeEnum;
  showError: boolean;
  setShowError: (showError: boolean) => void;
  isBeingEdited?: boolean;
  currentRuleState: Partial<CombinedColumnRuleInputParameters>;
  skipDefaultValue?: boolean;
  skipLabel?: boolean;
  disabled?: boolean;
  extraValue?: any;
}

export interface OptionDropdownInputProps extends BaseInputParametersProps {
  dropdownOptionsOverride?: { value: string; label: string }[];
}

export interface TextInputParameterProps extends BaseInputParametersProps {
  placeholderOverride?: string;
  readonly?: boolean;
  extraClassNames?: string;
  isBeingEdited?: boolean;
}

export interface BooleanInputProps extends BaseInputParametersProps {}

export interface ColumnDropdownInputProps extends BaseInputParametersProps {
  allColumnDefinitions: DecoratedScalingTableColumnDefinition[];
  currentColumnDefinition?: ColumnDefinitionInputV1;
  potentialColumnsNotYetCreated?: PotentialColumnDefinition[];
  nonStandardColumns?: ScalingTableColumnDefinition[];
  firstIsCreatable?: boolean;
  onRenameFirstColumn?: (newName: string) => void;
  firstColumnIsRestricted?: boolean;
  otherColumnsAreRestricted?: boolean;
  defaultDropdownDescription?: string;
  hidePotentialColumns?: boolean;
  showHiddenInputsOverride?: boolean;
  renderHiddenColumnToggle?: boolean;
  isLabelBold?: boolean;
  potentialTextColumnPlacement?: 'top' | 'bottom';
  tableDefinitionStore: TableDefinitionStore;
  tableDefinitionActions: TableDefinitionActions;
}

export interface ConfigureTreeCreationOrUpdatePseudoRulesProps {
  allColumnDefinitions: ScalingTableColumnDefinition[];
  currentRuleState: Partial<CombinedColumnRuleInputParameters>;
  setParameter: (parameter: RuleParameter, value: any, skipUpdateColumnDefToCreate: boolean) => void;
  currentColumnDefinition?: ColumnDefinitionInputV1;
  columnRuleToEdit?: ColumnRuleTreePathCreatorInputV1;
  columnType?: ColumnTypeEnum;
  getPotentialColumns: (
    columnDefinitionsToCreate: PotentialColumnDefinition[],
    potentialRootColumnPairIfNotAlreadySelectedForCreation?: {
      rootText: PotentialColumnDefinition;
      rootAsset: PotentialColumnDefinition;
    },
  ) => PotentialColumnDefinition[];
  setPotentialAdditionalColumns: React.Dispatch<React.SetStateAction<PotentialColumnDefinition[]>>;
  potentialAdditionalColumns: PotentialColumnDefinition[];
  fetchDecoratedColumns: (
    fetchAllowedColumnsForFirstInput: (
      columns: DecoratedScalingTableColumnDefinition[],
    ) => { columnName: string; description: string }[],
  ) => Promise<DecoratedScalingTableColumnDefinition[]>;
  setDecoratedColumnDefinitions: React.Dispatch<React.SetStateAction<DecoratedScalingTableColumnDefinition[]>>;
  decoratedColumnDefinitions: DecoratedScalingTableColumnDefinition[];
  setColumnDefinitionsToCreate: React.Dispatch<React.SetStateAction<PotentialColumnDefinition[]>>;
  columnDefinitionsToCreate: PotentialColumnDefinition[];
  showError: boolean;
  setShowError: (showError: boolean) => void;
  tableDefinitionStore: TableDefinitionStore;
  tableDefinitionActions: TableDefinitionActions;
}

export interface PropertyInputProps {
  currentPropertyName?: string;
  currentPropertyOperator?: string;
  currentPropertyValue?: string;
  propertyNameParameter: ParameterWithMetadata;
  propertyOperatorParameter: ParameterWithMetadata;
  propertyValueParameter: ParameterWithMetadata;
  setParameter: (parameter: RuleParameter, value: any) => void;
  isBeingEdited?: boolean;
  ruleWithMetadata: ColumnRuleWithMetadata;
  allColumnDefinitions: ScalingTableColumnDefinition[];
  currentColumnDefinition?: ColumnDefinitionInputV1;
  showError: boolean;
  setShowError: (showError: boolean) => void;
  currentRuleState: Partial<CombinedColumnRuleInputParameters>;
  tableDefinitionStore: TableDefinitionStore;
  tableDefinitionActions: TableDefinitionActions;
}

export interface CombinedInputProps
  extends TextInputParameterProps,
    ColumnDropdownInputProps,
    BooleanInputProps,
    OptionDropdownInputProps {}

export interface ColumnRuleInputWithUIMetadata extends ColumnRuleInputV1 {
  isCollapsed?: boolean;
}

export const CalculateOrDefineValuesRuleCategory = 'SCALING.RULE_CATEGORIES.CALCULATE_OR_DEFINE_VALUES';
export const FindDataRuleCategory = 'SCALING.RULE_CATEGORIES.FIND_DATA';
export const FindOrChangeTextRuleCategory = 'SCALING.RULE_CATEGORIES.FIND_OR_CHANGE_TEXT';
export const StructureDataRuleCategory = 'SCALING.RULE_CATEGORIES.STRUCTURE_DATA';

export const CanonicalCategoryOrder = [
  FindDataRuleCategory,
  CalculateOrDefineValuesRuleCategory,
  FindOrChangeTextRuleCategory,
  StructureDataRuleCategory,
];
